import Icon from "../../components/Icon"
import { formatDateShort, formatMoney } from "../../utils/format"
import { getCustomerInitials } from "./utbillsHelpers"
import { describeCompanyStatus, describeRequest, describeWaitingBills } from "./autopayApprovalDisplay"

const TONE_TEXT = {
  amber: "text-amber-700",
  emerald: "text-emerald-700",
  slate: "text-slate-500",
}

// Same switch as the auto-invoice list in Customers, sized for a thumb.
function AutopaySwitch({ checked, disabled, label, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
        checked ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  )
}

function WaitingBill({ bill, canPay, busy, onPay }) {
  return (
    <li className="flex items-center gap-2 rounded-xl border border-amber-100 bg-white px-2.5 py-2">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-slate-800">{bill.description || "Bill"}</span>
        <span className="block text-[10px] text-slate-500">
          {bill.date ? formatDateShort(bill.date) : "No date"}
          {bill.isOverdue && <span className="font-bold text-rose-600"> · Overdue</span>}
        </span>
      </span>
      <span className="shrink-0 text-xs font-black tabular-nums text-slate-900">{formatMoney(bill.outstandingAmount)}</span>
      <button
        type="button"
        onClick={() => onPay(bill)}
        disabled={!canPay || busy}
        className="shrink-0 rounded-lg bg-teal-600 px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        Pay
      </button>
    </li>
  )
}

// One company that bills the customer: its auto-pay switch and, while it is not
// approved, the bills waiting for the customer.
function AutopayCompanyRow({ company, walletBalance, busy, onToggle, onPay }) {
  const status = describeCompanyStatus(company)
  const waitingText = describeWaitingBills(company)
  const requestText = describeRequest(company)
  const hasWaiting = company.waitingBills?.length > 0
  const canPay = !company.locked && Number(walletBalance || 0) > 0

  return (
    <li className={`px-4 py-3 ${hasWaiting ? "bg-amber-50/60" : ""}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-black text-slate-600">
          {getCustomerInitials(company.providerName)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">{company.providerName}</span>
          <span className={`block text-[11px] font-semibold ${TONE_TEXT[status.tone] || TONE_TEXT.slate}`}>
            {status.label}
          </span>
          {(waitingText || requestText) && (
            <span className="block text-[11px] text-slate-500">
              {[waitingText, requestText].filter(Boolean).join(" · ")}
            </span>
          )}
        </span>
        {company.canChange ? (
          <AutopaySwitch
            checked={Boolean(company.approved)}
            disabled={busy}
            label={`Auto-pay for ${company.providerName}`}
            onChange={(next) => onToggle(company, next)}
          />
        ) : (
          <Icon name="lock-fill" size="0.85rem" className="shrink-0 text-slate-400" aria-hidden="true" />
        )}
      </div>

      {company.locked && (
        <p className="mt-2 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] text-slate-600">
          This company is locked, so its bills cannot be paid right now.
        </p>
      )}

      {hasWaiting && (
        <div className="mt-2 sm:ml-12">
          <ul className="space-y-1.5">
            {company.waitingBills.map((bill) => (
              <WaitingBill key={bill.invoiceId} bill={bill} canPay={canPay} busy={busy} onPay={onPay} />
            ))}
          </ul>
          {!company.locked && !(Number(walletBalance || 0) > 0) && (
            <p className="mt-1.5 text-[11px] text-slate-500">Top up your wallet to pay these bills.</p>
          )}
        </div>
      )}
    </li>
  )
}

export default AutopayCompanyRow
